import { effect, inject, LOCALE_ID, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngxs/store';
import { lastValueFrom } from 'rxjs';
import { authenticationControllerUpdateProfileV1 } from '@lexplus-authentication-api';
import { API_CONFIGURATION } from '@lexplus-environments';
import { ProfileState } from '../states/profile/profile-state';
import { ProfileStateSetBrowserLanguage, ProfileStateSetProfile } from '../states/profile/profile.actions';
import { CookieUtility } from '../utilities/cookie.utility';
import { notify } from '../utilities/notify.utility';
import { normalizeLanguage, SupportedLanguage } from '../functions/supported-language.function';
import { LocalizedRouteService } from './localized-route.service';
import { LanguageInterface } from '../models/language.interface';

/**
 * Decides which language the app runs in, remembers a chosen one and switches to it.
 *
 * Each language is a separate bundle under `/<language>/`, so a switch is a
 * full-page redirect, not a router navigation — {@link LocalizedRouteService}
 * performs it and keeps the current page.
 *
 * Three sources can name a language, in this order:
 *
 * 1. **The profile** — `AspNetUsers.SpracheID`, the account's language, and the
 *    one a signed-in user's choice is written back to. Applied through an effect,
 *    because the profile arrives after a sign-in, later than
 *    {@link initLanguageAndListeners} runs.
 * 2. **The cookie** — `lexplus_language`, the same choice mirrored locally, and
 *    kept in step with the account by the effect below. It carries the language
 *    across the sign-in screens, where there is no profile yet, and it survives a
 *    sign-out.
 * 3. **The browser** — `navigator.language`, for a visitor we know nothing about.
 *    Re-detected on every load rather than remembered, so it never outranks a
 *    profile or a real choice.
 *
 * The order only works because {@link changeLanguage} persists to the profile
 * **before** redirecting. While it did not, the profile re-applied the old
 * language on the next load and bounced every switch straight back.
 */
@Service()
export class LanguageService {
  private readonly localeId = inject(LOCALE_ID);
  private readonly store = inject(Store);
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfiguration = inject(API_CONFIGURATION);
  private readonly cookieUtility = inject(CookieUtility);
  private readonly localizedRouteService = inject(LocalizedRouteService);
  private readonly profile = this.store.selectSignal(ProfileState.profile);
  private readonly isLoggedIn = this.store.selectSignal(ProfileState.isLoggedIn);

  private readonly DEFAULT_LANGUAGE: SupportedLanguage = 'en';

  /**
   * Applies the account's language whenever it becomes known, and mirrors it into
   * the cookie.
   *
   * An effect and not a one-time read because the account can arrive after
   * {@link initLanguageAndListeners} has run: a page load rehydrates the profile
   * from `localStorage` before that, but a sign-in fetches it from the API well
   * after it.
   *
   * Mirroring keeps the cookie from going stale — the account may have been
   * changed on another device — so the two sources can never name different
   * languages and their precedence never has to be re-derived from whichever
   * happened to be read first.
   */
  private readonly profileLanguageEffect = effect(() => {
    const accountLanguage = this.accountLanguage;
    if (accountLanguage) {
      this.setCookie(accountLanguage);
      this.switchTo(accountLanguage);
    }
  });

  /**
   * Applies the language the current visitor should see. Called once from the
   * root component; the account is watched from then on by the effect above,
   * which also owns the case of the profile only arriving after this ran.
   */
  public initLanguageAndListeners(): void {
    const language = this.accountLanguage ?? this.chosenLanguage;
    if (language) {
      this.switchTo(language);
      return;
    }

    const browserLanguage = this.detectLanguageFromBrowser();
    this.store.dispatch(new ProfileStateSetBrowserLanguage(browserLanguage));
    this.switchTo(browserLanguage);
  }

  public detectLanguageFromBrowser(): SupportedLanguage {
    return normalizeLanguage(navigator.language) ?? this.DEFAULT_LANGUAGE;
  }

  public getDefaultLanguagesWithLabel(): LanguageInterface[] {
    return [
      { code: 'de', label: 'Deutsch' },
      { code: 'fr', label: 'Français' },
      { code: 'it', label: 'Italiano' },
      { code: 'en', label: 'English' },
    ];
  }

  /**
   * Switches to a language the user picked: writes it to the account when there
   * is one, records it in the cookie and leaves for that bundle.
   *
   * Both the redirect and the cookie wait for the account to be updated, and are
   * skipped when that fails: the language would be back to the old one on the
   * next load anyway, and a toast explaining that cannot be read across a page
   * reload.
   *
   * @param {string} newLanguage - The language to switch to; one we do not build
   *   falls back to the default rather than leaving the user on a bundle they
   *   did not ask for.
   * @returns {Promise<void>} Resolves once the switch has been persisted and the
   *   redirect issued.
   */
  public async changeLanguage(newLanguage: string): Promise<void> {
    const language = normalizeLanguage(newLanguage) ?? this.DEFAULT_LANGUAGE;

    // The account first: the cookie must not record a choice that did not stick,
    // or the next load would apply it and then be corrected back by the account.
    if (this.isLoggedIn() && !(await this.saveLanguageOnProfile(language))) {
      return;
    }

    this.setCookie(language);
    this.switchTo(language);
  }

  public readLanguageCookie(): string | null {
    return this.cookieUtility.readLanguageCookie();
  }

  public setCookie(language: string): void {
    this.cookieUtility.writeLanguageCookie(language);
  }

  /** The signed-in account's language, as far as we know one. */
  private get accountLanguage(): SupportedLanguage | null {
    return this.isLoggedIn() ? normalizeLanguage(this.profile()?.sprache) : null;
  }

  /** The language the user picked in the UI, if they ever did. */
  private get chosenLanguage(): SupportedLanguage | null {
    return normalizeLanguage(this.readLanguageCookie());
  }

  /**
   * Writes the language to `AspNetUsers.SpracheID` and puts the profile the API
   * returns back into the state.
   *
   * Updating the state is not cosmetic: the profile is persisted to
   * `localStorage` and only re-fetched on a sign-in, so a state left on the old
   * language would be read back after the redirect and send the user straight
   * back to it.
   *
   * @param {SupportedLanguage} language - The language to persist.
   * @returns {Promise<boolean>} Whether the account now carries it.
   */
  private async saveLanguageOnProfile(language: SupportedLanguage): Promise<boolean> {
    try {
      const response = await lastValueFrom(
        // The language alone. `LexplusUpdateProfileDto` carries only the three fields
        // a user may set on themselves, and the endpoint takes the account from the
        // token — there is nothing else to send and no id to get wrong.
        authenticationControllerUpdateProfileV1(this.httpClient, this.apiConfiguration.baseUrlAuth, {
          body: { sprache: language },
        }),
      );
      this.store.dispatch(new ProfileStateSetProfile(response.body));
      return true;
    } catch {
      notify.error($localize`:@@account.menu.language.saveFailed:Sprache konnte nicht gespeichert werden`, {
        description: $localize`:@@account.menu.language.saveFailedDescription:Die Sprache wurde nicht gewechselt. Bitte später erneut versuchen.`,
      });
      return false;
    }
  }

  /**
   * Leaves for another language's bundle, keeping the current page. Whether that
   * redirect is possible at all is LocalizedRouteService's call — on the
   * single-language dev-server there is no sibling bundle to go to.
   */
  private switchTo(language: SupportedLanguage): void {
    if (language === normalizeLanguage(this.localeId)) {
      return;
    }

    void this.localizedRouteService.navigateToLocale(language);
  }
}
